import React, { useState, useEffect, useCallback } from "react";
import Icon from "components/AppIcon";
import api from "../../../lib/api";
import { useAuth } from "../../../contexts/AuthContext";
import { useBranch } from "../../../contexts/BranchContext";
import { useSubscriptionLimits } from "../../../hooks/useSubscriptionLimits";
import PlanLimitBanner from "../../../components/PlanLimitBanner";

const roleColors = {
  trainer: "var(--color-accent)",
  staff: "var(--color-primary)",
};

const SPECIALIZATIONS = [
  "Yoga", "HIIT", "Strength Training", "Cardio", "Pilates",
  "CrossFit", "Zumba", "Boxing", "Swimming", "Nutrition",
];

function SkeletonRow() {
  return (
    <tr className="border-b border-[var(--color-border)]">
      {[...Array(8)]?.map((_, i) => (
        <td key={i} className="px-4 py-3">
          <div className="h-4 bg-[var(--color-muted)] rounded animate-pulse" style={{ width: i === 0 ? "120px" : i === 7 ? "80px" : "80px" }} />
        </td>
      ))}
    </tr>
  );
}

function StaffModal({ open, onClose, onSaved, editData, branches, vendorId, checkLimit, defaultBranchId }) {
  const [form, setForm] = useState({
    full_name: "", email: "", phone: "", role: "staff",
    branch_id: "", specialization: "", joining_date: "", notes: "", is_active: true,
    password: "", confirmPassword: "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [isLimitError, setIsLimitError] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    if (editData) {
      setForm({
        full_name: editData?.full_name || "",
        email: editData?.email || "",
        phone: editData?.phone || "",
        role: editData?.role || "staff",
        branch_id: editData?.branch_id || "",
        specialization: editData?.specialization || "",
        joining_date: editData?.joining_date || "",
        notes: editData?.notes || "",
        is_active: editData?.is_active !== false,
        password: "",
        confirmPassword: "",
      });
    } else {
      setForm({ full_name: "", email: "", phone: "", role: "staff", branch_id: defaultBranchId || "", specialization: "", joining_date: "", notes: "", is_active: true, password: "", confirmPassword: "" });
    }
    setError(null);
    setIsLimitError(false);
    setShowPassword(false);
    setShowConfirmPassword(false);
  }, [editData, open]);

  const handleChange = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const getPasswordStrength = (pwd) => {
    if (!pwd) return { score: 0, label: "", color: "" };
    let score = 0;
    if (pwd?.length >= 8) score++;
    if (/[A-Z]/?.test(pwd)) score++;
    if (/[0-9]/?.test(pwd)) score++;
    if (/[^A-Za-z0-9]/?.test(pwd)) score++;
    const levels = [
      { score: 0, label: "", color: "" },
      { score: 1, label: "Weak", color: "var(--color-error)" },
      { score: 2, label: "Fair", color: "var(--color-warning)" },
      { score: 3, label: "Good", color: "#3b82f6" },
      { score: 4, label: "Strong", color: "var(--color-success)" },
    ];
    return levels?.[score] || levels?.[0];
  };

  const passwordStrength = getPasswordStrength(form?.password);

  const handleSave = async () => {
    if (!form?.full_name?.trim()) { setError("Full name is required."); setIsLimitError(false); return; }
    if (!form?.email?.trim()) { setError("Email is required."); setIsLimitError(false); return; }
    if (!editData) {
      // Check subscription limit for new staff/trainer
      const limitType = form?.role === "trainer" ? "trainers" : "staff";
      const limitMsg = checkLimit?.(limitType);
      if (limitMsg) { setError(limitMsg); setIsLimitError(true); return; }

      if (!form?.password) { setError("Password is required."); setIsLimitError(false); return; }
      if (form?.password?.length < 8) { setError("Password must be at least 8 characters."); setIsLimitError(false); return; }
      if (form?.password !== form?.confirmPassword) { setError("Passwords do not match."); setIsLimitError(false); return; }
    }
    setSaving(true);
    setError(null);
    setIsLimitError(false);
    try {
      if (editData?.id) {
        // Update existing profile
        await api.put(`/staff/${editData?.id}`, {
          full_name: form?.full_name?.trim(),
          phone: form?.phone?.trim() || null,
          branch_id: form?.branch_id || null,
          specialization: form?.role === 'trainer' ? (form?.specialization || null) : null,
          joining_date: form?.joining_date || null,
          notes: form?.notes?.trim() || null,
        });
      } else {
        // Create new staff/trainer account via backend
        await api.post('/staff', {
          email: form?.email?.trim()?.toLowerCase(),
          password: form?.password,
          full_name: form?.full_name?.trim(),
          role: form?.role,
          phone: form?.phone?.trim() || null,
          branch_id: form?.branch_id || null,
          specialization: form?.role === 'trainer' ? (form?.specialization || null) : null,
          joining_date: form?.joining_date || null,
          notes: form?.notes?.trim() || null,
        });
      }
      onSaved();
      onClose();
    } catch (err) {
      console.error("Save staff error:", err);
      setError(err?.message || "Failed to save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-[var(--color-card)] border border-[var(--color-border)] rounded-[var(--radius-md)] shadow-[var(--shadow-lg)] w-full max-w-lg max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-[var(--color-border)]">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-md bg-[var(--color-primary)]/15 flex items-center justify-center">
              <Icon name={editData ? "UserCog" : "UserPlus"} size={14} color="var(--color-primary)" />
            </div>
            <h3 className="text-sm font-semibold font-[var(--font-heading)] text-[var(--color-foreground)]">
              {editData ? "Edit Staff Member" : "Add New Staff / Trainer"}
            </h3>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-[var(--color-muted)] transition-colors">
            <Icon name="X" size={16} color="var(--color-muted-foreground)" />
          </button>
        </div>

        {/* Body */}
        <div className="p-4 space-y-3">
          {error && isLimitError ? (
            <PlanLimitBanner message={error} />
          ) : error ? (
            <div className="p-2.5 bg-[var(--color-error)]/10 border border-[var(--color-error)]/20 rounded-md">
              <p className="text-xs text-[var(--color-error)] font-[var(--font-caption)]">{error}</p>
            </div>
          ) : null}

          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <label className="block text-xs font-medium font-[var(--font-caption)] text-[var(--color-foreground)] mb-1">Full Name *</label>
              <input
                type="text"
                value={form?.full_name}
                onChange={e => handleChange('full_name', e?.target?.value)}
                placeholder="e.g. John Smith"
                className="w-full px-3 py-2 text-sm border border-[var(--color-border)] rounded-md bg-[var(--color-input)] text-[var(--color-foreground)] placeholder-[var(--color-muted-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]"
              />
            </div>

            <div className="col-span-2">
              <label className="block text-xs font-medium font-[var(--font-caption)] text-[var(--color-foreground)] mb-1">Email *</label>
              <input
                type="email"
                value={form?.email}
                onChange={e => handleChange('email', e?.target?.value)}
                placeholder="john@example.com"
                disabled={!!editData}
                className="w-full px-3 py-2 text-sm border border-[var(--color-border)] rounded-md bg-[var(--color-input)] text-[var(--color-foreground)] placeholder-[var(--color-muted-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)] disabled:opacity-60 disabled:cursor-not-allowed"
              />
            </div>

            {/* Password fields — only shown when adding new staff */}
            {!editData && (
              <>
                <div className="col-span-2">
                  <label className="block text-xs font-medium font-[var(--font-caption)] text-[var(--color-foreground)] mb-1">Password *</label>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      value={form?.password}
                      onChange={e => handleChange('password', e?.target?.value)}
                      placeholder="Min. 8 characters"
                      className="w-full px-3 py-2 pr-9 text-sm border border-[var(--color-border)] rounded-md bg-[var(--color-input)] text-[var(--color-foreground)] placeholder-[var(--color-muted-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(v => !v)}
                      className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)]"
                    >
                      <Icon name={showPassword ? "EyeOff" : "Eye"} size={14} />
                    </button>
                  </div>
                  {/* Password strength indicator */}
                  {form?.password && (
                    <div className="mt-1.5 space-y-1">
                      <div className="flex gap-1">
                        {[1,2,3,4]?.map(i => (
                          <div
                            key={i}
                            className="h-1 flex-1 rounded-full transition-all"
                            style={{ backgroundColor: i <= passwordStrength?.score ? passwordStrength?.color : 'var(--color-border)' }}
                          />
                        ))}
                      </div>
                      {passwordStrength?.label && (
                        <p className="text-xs font-[var(--font-caption)]" style={{ color: passwordStrength?.color }}>
                          {passwordStrength?.label}
                        </p>
                      )}
                    </div>
                  )}
                </div>

                <div className="col-span-2">
                  <label className="block text-xs font-medium font-[var(--font-caption)] text-[var(--color-foreground)] mb-1">Confirm Password *</label>
                  <div className="relative">
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      value={form?.confirmPassword}
                      onChange={e => handleChange('confirmPassword', e?.target?.value)}
                      placeholder="Re-enter password"
                      className="w-full px-3 py-2 pr-9 text-sm border border-[var(--color-border)] rounded-md bg-[var(--color-input)] text-[var(--color-foreground)] placeholder-[var(--color-muted-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(v => !v)}
                      className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)]"
                    >
                      <Icon name={showConfirmPassword ? "EyeOff" : "Eye"} size={14} />
                    </button>
                  </div>
                  {form?.confirmPassword && form?.password !== form?.confirmPassword && (
                    <p className="text-xs text-[var(--color-error)] mt-1 font-[var(--font-caption)]">Passwords do not match</p>
                  )}
                  {form?.confirmPassword && form?.password === form?.confirmPassword && form?.password?.length >= 8 && (
                    <p className="text-xs text-[var(--color-success)] mt-1 font-[var(--font-caption)]">✓ Passwords match</p>
                  )}
                </div>
              </>
            )}

            <div>
              <label className="block text-xs font-medium font-[var(--font-caption)] text-[var(--color-foreground)] mb-1">Phone</label>
              <input
                type="tel"
                value={form?.phone}
                onChange={e => handleChange('phone', e?.target?.value)}
                placeholder="+1 234 567 8900"
                className="w-full px-3 py-2 text-sm border border-[var(--color-border)] rounded-md bg-[var(--color-input)] text-[var(--color-foreground)] placeholder-[var(--color-muted-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]"
              />
            </div>

            <div>
              <label className="block text-xs font-medium font-[var(--font-caption)] text-[var(--color-foreground)] mb-1">Role *</label>
              <select
                value={form?.role}
                onChange={e => handleChange('role', e?.target?.value)}
                className="w-full px-3 py-2 text-sm border border-[var(--color-border)] rounded-md bg-[var(--color-input)] text-[var(--color-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]"
              >
                <option value="staff">Staff</option>
                <option value="trainer">Trainer</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium font-[var(--font-caption)] text-[var(--color-foreground)] mb-1">Branch</label>
              <select
                value={form?.branch_id}
                onChange={e => handleChange('branch_id', e?.target?.value)}
                className="w-full px-3 py-2 text-sm border border-[var(--color-border)] rounded-md bg-[var(--color-input)] text-[var(--color-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]"
              >
                <option value="">No Branch</option>
                {branches?.map(b => (
                  <option key={b?.id} value={b?.id}>{b?.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium font-[var(--font-caption)] text-[var(--color-foreground)] mb-1">Joining Date</label>
              <input
                type="date"
                value={form?.joining_date}
                onChange={e => handleChange('joining_date', e?.target?.value)}
                className="w-full px-3 py-2 text-sm border border-[var(--color-border)] rounded-md bg-[var(--color-input)] text-[var(--color-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]"
              />
            </div>

            {form?.role === 'trainer' && (
              <div className="col-span-2">
                <label className="block text-xs font-medium font-[var(--font-caption)] text-[var(--color-foreground)] mb-1">Specialization</label>
                <select
                  value={form?.specialization}
                  onChange={e => handleChange('specialization', e?.target?.value)}
                  className="w-full px-3 py-2 text-sm border border-[var(--color-border)] rounded-md bg-[var(--color-input)] text-[var(--color-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]"
                >
                  <option value="">Select specialization</option>
                  {SPECIALIZATIONS?.map(s => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
            )}

            {editData && (
              <div className="col-span-2 flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={form?.is_active}
                  onChange={e => handleChange('is_active', e?.target?.checked)}
                  className="w-4 h-4 rounded border-[var(--color-border)] text-[var(--color-primary)] focus:ring-[var(--color-ring)]"
                />
                <label htmlFor="is_active" className="text-xs font-medium font-[var(--font-caption)] text-[var(--color-foreground)]">Active (uncheck to mark as Inactive)</label>
              </div>
            )}

            <div className="col-span-2">
              <label className="block text-xs font-medium font-[var(--font-caption)] text-[var(--color-foreground)] mb-1">Notes</label>
              <textarea
                value={form?.notes}
                onChange={e => handleChange('notes', e?.target?.value)}
                placeholder="Any additional notes..."
                rows={2}
                className="w-full px-3 py-2 text-sm border border-[var(--color-border)] rounded-md bg-[var(--color-input)] text-[var(--color-foreground)] placeholder-[var(--color-muted-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)] resize-none"
              />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 p-4 border-t border-[var(--color-border)]">
          <button
            onClick={onClose}
            className="px-4 py-2 text-xs font-medium font-[var(--font-caption)] border border-[var(--color-border)] rounded-md text-[var(--color-foreground)] hover:bg-[var(--color-muted)] transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-1.5 px-4 py-2 text-xs font-medium bg-[var(--color-primary)] text-white rounded-md hover:bg-[var(--color-secondary)] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {saving && <Icon name="Loader2" size={12} color="white" className="animate-spin" />}
            {saving ? "Saving..." : editData ? "Save Changes" : "Add Staff Member"}
          </button>
        </div>
      </div>
    </div>
  );
}

function DeleteConfirmModal({ open, onClose, onConfirm, staffName, hasMembers, deleting }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-[var(--color-card)] border border-[var(--color-border)] rounded-[var(--radius-md)] shadow-[var(--shadow-lg)] w-full max-w-sm">
        <div className="p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 rounded-full bg-[var(--color-error)]/15 flex items-center justify-center flex-shrink-0">
              <Icon name="UserX" size={16} color="var(--color-error)" />
            </div>
            <div>
              <h3 className="text-sm font-semibold font-[var(--font-heading)] text-[var(--color-foreground)]">
                {hasMembers ? "Cannot Delete Staff" : "Remove Staff Member?"}
              </h3>
              <p className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)] mt-0.5">{staffName}</p>
            </div>
          </div>
          {hasMembers ? (
            <div className="p-3 bg-[var(--color-warning)]/10 border border-[var(--color-warning)]/20 rounded-md mb-4">
              <p className="text-xs text-[var(--color-warning)] font-[var(--font-caption)]">
                ⚠️ Reassign their members before deleting. This trainer has active members assigned to them.
              </p>
            </div>
          ) : (
            <p className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)] mb-4">
              This will permanently remove <strong className="text-[var(--color-foreground)]">{staffName}</strong> from your roster. They will lose access to the system.
            </p>
          )}
          <div className="flex items-center justify-end gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 text-xs font-medium border border-[var(--color-border)] rounded-md text-[var(--color-foreground)] hover:bg-[var(--color-muted)] transition-colors"
            >
              {hasMembers ? "Close" : "Cancel"}
            </button>
            {!hasMembers && (
              <button
                onClick={onConfirm}
                disabled={deleting}
                className="flex items-center gap-1.5 px-4 py-2 text-xs font-medium bg-[var(--color-error)] text-white rounded-md hover:opacity-90 transition-opacity disabled:opacity-60"
              >
                {deleting && <Icon name="Loader2" size={12} color="white" className="animate-spin" />}
                {deleting ? "Removing..." : "Yes, Remove"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function StaffRoster() {
  const { user } = useAuth();
  const { selectedBranchId } = useBranch();
  const [staffList, setStaffList] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filters
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [branchFilter, setBranchFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");

  // Modals
  const [showModal, setShowModal] = useState(false);
  const [editData, setEditData] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteHasMembers, setDeleteHasMembers] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const { checkLimit, refetch: refetchLimits } = useSubscriptionLimits(user?.id);

  const fetchBranches = useCallback(async () => {
    const { data } = await api.get('/branches');
    setBranches(data || []);
  }, []);

  const fetchStaff = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params = {};
      if (selectedBranchId) params.branch_id = selectedBranchId;
      const { data } = await api.get('/staff', { params });
      setStaffList(data || []);
    } catch (err) {
      console.error('Error fetching staff:', err);
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  }, [selectedBranchId]);

  useEffect(() => {
    fetchBranches();
    fetchStaff();
  }, [fetchBranches, fetchStaff]);

  // Client-side filtering
  const filtered = staffList?.filter(s => {
    const matchSearch = !search || s?.full_name?.toLowerCase()?.includes(search?.toLowerCase()) || s?.email?.toLowerCase()?.includes(search?.toLowerCase());
    const matchRole = roleFilter === "all" || s?.role === roleFilter;
    const matchBranch = branchFilter === "all" || s?.branch_id === branchFilter;
    const matchStatus = statusFilter === "all" || (statusFilter === "active" ? s?.is_active : !s?.is_active);
    return matchSearch && matchRole && matchBranch && matchStatus;
  });

  const hasFilters = search || roleFilter !== "all" || branchFilter !== "all" || statusFilter !== "all";

  const clearFilters = () => {
    setSearch("");
    setRoleFilter("all");
    setBranchFilter("all");
    setStatusFilter("all");
  };

  const handleEdit = (staff) => {
    setEditData(staff);
    setShowModal(true);
  };

  const handleAddNew = () => {
    setEditData(null);
    setShowModal(true);
  };

  const handleDeleteClick = async (staff) => {
    // Check if trainer has assigned members
    const { data: assignedMembers } = await api.get('/members', { params: { trainer_id: staff?.id, limit: 1 } });
    setDeleteHasMembers((assignedMembers || [])?.length > 0);
    setDeleteTarget(staff);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget?.id) return;
    setDeleting(true);
    try {
      await api.delete(`/staff/${deleteTarget?.id}`);
      setDeleteTarget(null);
      fetchStaff();
      refetchLimits();
    } catch (err) {
      console.error("Delete error:", err);
    } finally {
      setDeleting(false);
    }
  };

  const getBranchName = (branchId) => branches?.find(b => b?.id === branchId)?.name || "—";

  return (
    <>
      <div className="bg-[var(--color-card)] border border-[var(--color-border)] rounded-[var(--radius-md)] shadow-[var(--shadow-sm)] overflow-hidden">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 p-4 border-b border-[var(--color-border)]">
          <div>
            <h3 className="text-sm font-semibold font-[var(--font-heading)] text-[var(--color-foreground)]">Staff Roster</h3>
            <p className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)] mt-0.5">{staffList?.length} team member{staffList?.length !== 1 ? "s" : ""}</p>
          </div>
          <button
            onClick={handleAddNew}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-[var(--color-primary)] text-white rounded-md hover:bg-[var(--color-secondary)] transition-colors"
          >
            <Icon name="UserPlus" size={13} color="white" />
            Add Staff / Trainer
          </button>
        </div>

        {/* Search & Filters */}
        <div className="p-4 border-b border-[var(--color-border)] bg-[var(--color-muted)]/20">
          <div className="flex flex-col sm:flex-row gap-2">
            {/* Search */}
            <div className="relative flex-1">
              <Icon name="Search" size={14} color="var(--color-muted-foreground)" className="absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
              <input
                type="text"
                value={search}
                onChange={e => setSearch(e?.target?.value)}
                placeholder="Search by name or email..."
                className="w-full pl-8 pr-3 py-1.5 text-xs border border-[var(--color-border)] rounded-md bg-[var(--color-input)] text-[var(--color-foreground)] placeholder-[var(--color-muted-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]"
              />
            </div>

            {/* Role Filter */}
            <select
              value={roleFilter}
              onChange={e => setRoleFilter(e?.target?.value)}
              className="px-2 py-1.5 text-xs border border-[var(--color-border)] rounded-md bg-[var(--color-input)] text-[var(--color-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]"
            >
              <option value="all">All Roles</option>
              <option value="staff">Staff</option>
              <option value="trainer">Trainer</option>
            </select>

            {/* Branch Filter */}
            <select
              value={branchFilter}
              onChange={e => setBranchFilter(e?.target?.value)}
              className="px-2 py-1.5 text-xs border border-[var(--color-border)] rounded-md bg-[var(--color-input)] text-[var(--color-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]"
            >
              <option value="all">All Branches</option>
              {branches?.map(b => (
                <option key={b?.id} value={b?.id}>{b?.name}</option>
              ))}
            </select>

            {/* Status Filter */}
            <select
              value={statusFilter}
              onChange={e => setStatusFilter(e?.target?.value)}
              className="px-2 py-1.5 text-xs border border-[var(--color-border)] rounded-md bg-[var(--color-input)] text-[var(--color-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>

            {/* Clear Filters */}
            {hasFilters && (
              <button
                onClick={clearFilters}
                className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium border border-[var(--color-border)] rounded-md text-[var(--color-muted-foreground)] hover:text-[var(--color-foreground)] hover:bg-[var(--color-muted)] transition-colors whitespace-nowrap"
              >
                <Icon name="X" size={11} color="currentColor" />
                Clear
              </button>
            )}
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="mx-4 mt-3 p-2.5 bg-[var(--color-error)]/10 border border-[var(--color-error)]/20 rounded-md">
            <p className="text-xs text-[var(--color-error)] font-[var(--font-caption)]">{error}</p>
          </div>
        )}

        {/* Table */}
        <div className="overflow-x-auto">
          {loading ? (
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[var(--color-muted)] border-b border-[var(--color-border)]">
                  {["Name", "Role", "Branch", "Specialization", "Joining Date", "Phone", "Status", "Actions"]?.map(h => (
                    <th key={h} className="px-4 py-2.5 text-left text-xs font-semibold font-[var(--font-caption)] text-[var(--color-muted-foreground)] uppercase tracking-wide whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[...Array(4)]?.map((_, i) => <SkeletonRow key={i} />)}
              </tbody>
            </table>
          ) : filtered?.length === 0 ? (
            <div className="p-12 text-center">
              <div className="w-12 h-12 rounded-full bg-[var(--color-muted)] flex items-center justify-center mx-auto mb-3">
                <Icon name="Users" size={20} color="var(--color-muted-foreground)" />
              </div>
              <p className="text-sm font-medium font-[var(--font-heading)] text-[var(--color-foreground)] mb-1">
                {hasFilters ? "No staff match your filters" : "No staff members yet"}
              </p>
              <p className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)] mb-4">
                {hasFilters ? "Try adjusting or clearing your filters." : "Add your first staff member to get started."}
              </p>
              {!hasFilters && (
                <button
                  onClick={handleAddNew}
                  className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-medium bg-[var(--color-primary)] text-white rounded-md hover:bg-[var(--color-secondary)] transition-colors"
                >
                  <Icon name="UserPlus" size={13} color="white" />
                  Add your first staff member
                </button>
              )}
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[var(--color-muted)] border-b border-[var(--color-border)]">
                  {["Name", "Role", "Branch", "Specialization", "Joining Date", "Phone", "Status", "Actions"]?.map(h => (
                    <th key={h} className="px-4 py-2.5 text-left text-xs font-semibold font-[var(--font-caption)] text-[var(--color-muted-foreground)] uppercase tracking-wide whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--color-border)]">
                {filtered?.map(s => (
                  <tr key={s?.id} className="hover:bg-[var(--color-muted)]/30 transition-colors">
                    {/* Name */}
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div
                          className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0"
                          style={{ backgroundColor: roleColors?.[s?.role] || "var(--color-primary)" }}
                        >
                          {s?.full_name?.charAt(0)?.toUpperCase()}
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium text-xs text-[var(--color-foreground)] font-[var(--font-body)] whitespace-nowrap">{s?.full_name}</p>
                          <p className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)] truncate max-w-[140px]">{s?.email}</p>
                        </div>
                      </div>
                    </td>
                    {/* Role */}
                    <td className="px-4 py-3">
                      <span
                        className="px-2 py-0.5 rounded-full text-xs font-medium font-[var(--font-caption)] capitalize"
                        style={{ backgroundColor: `${roleColors?.[s?.role] || "var(--color-primary)"}18`, color: roleColors?.[s?.role] || "var(--color-primary)" }}
                      >
                        {s?.role}
                      </span>
                    </td>
                    {/* Branch */}
                    <td className="px-4 py-3 text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)] whitespace-nowrap">
                      {getBranchName(s?.branch_id)}
                    </td>
                    {/* Specialization */}
                    <td className="px-4 py-3 text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)] whitespace-nowrap">
                      {s?.specialization || "—"}
                    </td>
                    {/* Joining Date */}
                    <td className="px-4 py-3 text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)] whitespace-nowrap">
                      {s?.joining_date ? new Date(s?.joining_date)?.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : "—"}
                    </td>
                    {/* Phone */}
                    <td className="px-4 py-3 text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)] whitespace-nowrap">
                      {s?.phone || "—"}
                    </td>
                    {/* Status */}
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 text-xs font-medium font-[var(--font-caption)] ${s?.is_active ? "text-[var(--color-success)]" : "text-[var(--color-warning)]"}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${s?.is_active ? "bg-[var(--color-success)]" : "bg-[var(--color-warning)]"}`} />
                        {s?.is_active ? "Active" : "Inactive"}
                      </span>
                    </td>
                    {/* Actions */}
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => handleEdit(s)}
                          title="Edit"
                          className="p-1.5 rounded hover:bg-[var(--color-primary)]/10 transition-colors"
                        >
                          <Icon name="Pencil" size={13} color="var(--color-primary)" />
                        </button>
                        <button
                          onClick={() => handleDeleteClick(s)}
                          title="Delete"
                          className="p-1.5 rounded hover:bg-[var(--color-error)]/10 transition-colors"
                        >
                          <Icon name="Trash2" size={13} color="var(--color-error)" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer count */}
        {!loading && filtered?.length > 0 && (
          <div className="px-4 py-2.5 border-t border-[var(--color-border)] bg-[var(--color-muted)]/20">
            <p className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)]">
              Showing {filtered?.length} of {staffList?.length} staff member{staffList?.length !== 1 ? "s" : ""}
            </p>
          </div>
        )}
      </div>
      {/* Add/Edit Modal */}
      <StaffModal
        open={showModal}
        onClose={() => { setShowModal(false); setEditData(null); }}
        onSaved={() => { fetchStaff(); refetchLimits(); }}
        editData={editData}
        branches={branches}
        vendorId={user?.id}
        checkLimit={checkLimit}
        defaultBranchId={selectedBranchId}
      />
      {/* Delete Confirm Modal */}
      <DeleteConfirmModal
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDeleteConfirm}
        staffName={deleteTarget?.full_name}
        hasMembers={deleteHasMembers}
        deleting={deleting}
      />
    </>
  );
}